import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Settings, Save, Bell, Lock, Globe, Mail, Database, Users, ArrowLeft, Award, Eye, Download } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { db } from '../../lib/db'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'

interface CredentialDoc {
  key: string
  title: string
  organization: string
  available: boolean
}

interface SystemSettings {
  site_name: string
  site_description: string
  contact_email: string
  contact_phone: string
  business_address: string
  timezone: string
  currency: string
  email_notifications: boolean
  sms_notifications: boolean
  booking_lead_time: number
  session_cancellation_hours: number
  auto_confirm_bookings: boolean
  maintenance_mode: boolean
}

const AdminSettings: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const { user: instantUser } = db.useAuth()
  const credentialToken = instantUser?.refresh_token
  const navigate = useNavigate()
  const [credentials, setCredentials] = useState<CredentialDoc[]>([])
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [credentialsError, setCredentialsError] = useState('')
  const [openingKey, setOpeningKey] = useState<string | null>(null)
  const [settings, setSettings] = useState<SystemSettings>({
    site_name: 'Fit by Cassie',
    site_description: 'Professional fitness training and meal planning',
    contact_email: 'hello@fitbycassie.com',
    contact_phone: '+1 (555) 123-4567',
    business_address: '123 Fitness Street, Wellness City, WC 12345',
    timezone: 'America/New_York',
    currency: 'USD',
    email_notifications: true,
    sms_notifications: false,
    booking_lead_time: 24,
    session_cancellation_hours: 12,
    auto_confirm_bookings: true,
    maintenance_mode: false
  })
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('general')
  const [saveMessage, setSaveMessage] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      // In a real app, you'd load these from a system_settings table
      // For now, we'll use default values
      console.log('Settings loaded')
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      // In a real app, you'd save these to a system_settings table
      // For now, we'll just simulate a save
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      setSaveMessage('Settings saved successfully!')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (error) {
      console.error('Error saving settings:', error)
      setSaveMessage('Error saving settings')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof SystemSettings, value: string | boolean | number) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  const tabs = [
    { id: 'general', name: 'General', icon: Globe },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'booking', name: 'Booking', icon: Users },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'credentials', name: 'Credentials', icon: Award },
    { id: 'system', name: 'System', icon: Database }
  ]

  useEffect(() => {
    if (activeTab !== 'credentials' || !credentialToken) return

    let cancelled = false
    setCredentialsLoading(true)
    setCredentialsError('')

    fetch('/api/admin/credentials?list=1', {
      headers: { Authorization: `Bearer ${credentialToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Could not load your credentials list.')
        return res.json()
      })
      .then((data) => {
        if (!cancelled) setCredentials(data.credentials || [])
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) setCredentialsError('Could not load your credentials list.')
      })
      .finally(() => {
        if (!cancelled) setCredentialsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeTab, credentialToken])

  const openCredential = async (key: string, title: string, mode: 'preview' | 'download') => {
    if (!credentialToken) return
    setOpeningKey(key)
    setCredentialsError('')

    try {
      const res = await fetch(`/api/admin/credentials?key=${encodeURIComponent(key)}`, {
        headers: { Authorization: `Bearer ${credentialToken}` },
      })
      if (!res.ok) throw new Error('Could not load that certificate.')

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)

      if (mode === 'preview') {
        window.open(url, '_blank', 'noopener')
      } else {
        const link = document.createElement('a')
        link.href = url
        link.download = `${title.replace(/\s+/g, '-').toLowerCase()}.webp`
        document.body.appendChild(link)
        link.click()
        link.remove()
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (err) {
      console.error(err)
      setCredentialsError('Could not open that certificate right now.')
    } finally {
      setOpeningKey(null)
    }
  }

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-4">Site Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
            <input
              type="text"
              value={settings.site_name}
              onChange={(e) => handleInputChange('site_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
            <input
              type="email"
              value={settings.contact_email}
              onChange={(e) => handleInputChange('contact_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
            <input
              type="tel"
              value={settings.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            >
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
          <textarea
            value={settings.site_description}
            onChange={(e) => handleInputChange('site_description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
          <textarea
            value={settings.business_address}
            onChange={(e) => handleInputChange('business_address', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
          />
        </div>
      </div>
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-ink">Email Notifications</h4>
              <p className="text-sm text-gray-600">Receive notifications via email</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.email_notifications}
                onChange={(e) => handleInputChange('email_notifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ink"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-ink">SMS Notifications</h4>
              <p className="text-sm text-gray-600">Receive notifications via text message</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.sms_notifications}
                onChange={(e) => handleInputChange('sms_notifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ink"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderBookingSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-4">Booking Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Booking Lead Time (hours)</label>
            <input
              type="number"
              value={settings.booking_lead_time}
              onChange={(e) => handleInputChange('booking_lead_time', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum hours before a session can be booked</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cancellation Notice (hours)</label>
            <input
              type="number"
              value={settings.session_cancellation_hours}
              onChange={(e) => handleInputChange('session_cancellation_hours', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Hours notice required to cancel a session</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-ink">Auto-confirm Bookings</h4>
              <p className="text-sm text-gray-600">Automatically confirm new bookings without manual approval</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.auto_confirm_bookings}
                onChange={(e) => handleInputChange('auto_confirm_bookings', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-ink"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-4">Security & Access</h3>
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <Lock className="h-5 w-5 text-yellow-600 mr-2" />
              <h4 className="font-medium text-yellow-800">Admin Access</h4>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Admin access is currently configured through the AdminAuthContext. 
              Only Cassie's account (hello@fitbycassie.com) has automatic admin access.
            </p>
          </div>
          <div className="p-4 bg-brand-soft border border-brand/20 rounded-lg">
            <div className="flex items-center">
              <Database className="h-5 w-5 text-brand mr-2" />
              <h4 className="font-medium text-blue-800">Database Security</h4>
            </div>
            <p className="text-sm text-brand-dark mt-1">
              Row Level Security (RLS) is enabled on all sensitive tables. 
              User data is protected through Supabase authentication policies.
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderCredentialsSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-2">
          My Professional Credentials
        </h3>
        <p className="text-sm text-gray-600 mb-5">
          These certificate files are private. Only your signed-in admin
          account can preview or download them — the public About page no
          longer links to the images, and the server re-checks your admin
          session on every request here.
        </p>

        {credentialsError && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-800 text-sm">
            {credentialsError}
          </div>
        )}

        {credentialsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-3">
            {credentials.map((cred) => (
              <div
                key={cred.key}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div>
                  <p className="font-medium text-ink">{cred.title}</p>
                  <p className="text-sm text-gray-500">{cred.organization}</p>
                  {!cred.available && (
                    <p className="text-xs text-red-600 mt-1">File not found in private storage.</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    disabled={!cred.available || openingKey === cred.key}
                    onClick={() => openCredential(cred.key, cred.title, 'preview')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    disabled={!cred.available || openingKey === cred.key}
                    onClick={() => openCredential(cred.key, cred.title, 'download')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-ink text-white hover:bg-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-ink mb-4">System Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
            <select
              value={settings.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </div>
          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
            <div>
              <h4 className="font-medium text-red-800">Maintenance Mode</h4>
              <p className="text-sm text-red-700">
                Enable maintenance mode to temporarily disable public access
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.maintenance_mode}
                onChange={(e) => handleInputChange('maintenance_mode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general': return renderGeneralSettings()
      case 'notifications': return renderNotificationSettings()
      case 'booking': return renderBookingSettings()
      case 'security': return renderSecuritySettings()
      case 'credentials': return renderCredentialsSettings()
      case 'system': return renderSystemSettings()
      default: return renderGeneralSettings()
    }
  }

  return (
    <div className="panel-page">
      <div className="panel-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="mb-4 flex items-center text-gray-600 hover:text-ink transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Admin Dashboard
              </button>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="panel-title flex items-center">
                <Settings className="mr-3 h-8 w-8" />
                System Settings
              </h1>
              <p className="mt-2 text-gray-600">
                Configure system preferences and settings
              </p>
            </div>
            <button 
              onClick={saveSettings}
              disabled={loading}
              className="bg-ink text-white px-4 py-2 rounded-lg hover:bg-ink-soft transition-colors flex items-center disabled:opacity-50"
            >
              <Save className="mr-2 h-4 w-4" />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
          {saveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 p-3 rounded-lg ${
                saveMessage.includes('Error') 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {saveMessage}
            </motion.div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <GlassCard className="p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-brand-dark border border-brand/20'
                        : 'text-gray-600 hover:text-ink hover:bg-cream'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 mr-3" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </GlassCard>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <GlassCard className="p-6">
                {renderTabContent()}
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminSettings 